import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { 
  ArrowLeft, Download, Share2, Edit, Copy, 
  ChevronLeft, ChevronRight, Presentation 
} from 'lucide-react';
import { Button } from '../../../components/ui/Button';

interface SlideData {
  id: number;
  imageUrl: string;
  notes?: string;
}

const SlideDeckPreview: React.FC = () => {
  const { templateId } = useParams<{ templateId: string }>();
  const [currentSlide, setCurrentSlide] = useState(0);
  const [loading, setLoading] = useState(true);
  const [slides, setSlides] = useState<SlideData[]>([]);
  const [templateName, setTemplateName] = useState('');
  const [templateDescription, setTemplateDescription] = useState('');

  // Simulated data loading
  useEffect(() => {
    // In a real implementation, this would fetch data from an API
    const loadData = async () => {
      setLoading(true);
      
      // Simulate API call delay
      await new Promise(resolve => setTimeout(resolve, 800));
      
      // Mock data based on templateId
      let mockData = {
        name: 'Retirement Planning Essentials',
        description: 'A comprehensive slide deck covering all aspects of retirement planning for clients nearing retirement.',
        slides: Array.from({ length: 12 }, (_, i) => ({
          id: i + 1,
          imageUrl: `https://picsum.photos/seed/${templateId || 'default'}-${i}/800/450`,
          notes: i % 3 === 0 ? 'Speaker notes for this slide explaining key points and talking points.' : undefined
        }))
      };
      
      setTemplateName(mockData.name);
      setTemplateDescription(mockData.description);
      setSlides(mockData.slides);
      setLoading(false);
    };
    
    loadData();
  }, [templateId]);

  const goToNextSlide = () => {
    if (currentSlide < slides.length - 1) {
      setCurrentSlide(currentSlide + 1);
    }
  };

  const goToPrevSlide = () => {
    if (currentSlide > 0) {
      setCurrentSlide(currentSlide - 1);
    }
  };

  // Handle keyboard navigation
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'ArrowRight') {
        goToNextSlide();
      } else if (e.key === 'ArrowLeft') {
        goToPrevSlide();
      }
    };
    
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [currentSlide, slides.length]);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-screen bg-gray-50">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto"></div>
          <p className="mt-4 text-gray-600">Loading slide deck...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-100 flex flex-col">
      {/* Header */}
      <div className="bg-white border-b border-gray-200 py-4 px-6">
        <div className="flex justify-between items-center">
          <div className="flex items-center">
            <Link to="/dashboard/landing-pages" className="mr-4">
              <ArrowLeft className="h-5 w-5 text-gray-600 hover:text-gray-900" />
            </Link>
            <div>
              <h1 className="text-xl font-semibold text-gray-900">{templateName}</h1>
              <p className="text-sm text-gray-500">{templateDescription}</p>
            </div>
          </div>
          <div className="flex items-center space-x-3">
            <Button variant="outline" className="flex items-center">
              <Edit className="h-4 w-4 mr-2" />
              Edit
            </Button>
            <Button variant="outline" className="flex items-center">
              <Copy className="h-4 w-4 mr-2" />
              Duplicate
            </Button>
            <Button variant="outline" className="flex items-center">
              <Share2 className="h-4 w-4 mr-2" />
              Share
            </Button>
            <Button className="flex items-center">
              <Download className="h-4 w-4 mr-2" />
              Download
            </Button>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="flex-grow flex flex-col md:flex-row">
        {/* Slide Navigator */}
        <div className="w-full md:w-64 bg-gray-800 overflow-y-auto">
          <div className="p-4">
            <h2 className="text-white text-sm font-medium mb-4">Slides</h2>
            <div className="space-y-2">
              {slides.map((slide, index) => (
                <div
                  key={slide.id}
                  className={`cursor-pointer rounded-lg overflow-hidden ${
                    currentSlide === index ? 'ring-2 ring-primary' : ''
                  }`}
                  onClick={() => setCurrentSlide(index)}
                >
                  <div className="relative">
                    <img
                      src={slide.imageUrl}
                      alt={`Slide ${index + 1}`}
                      className="w-full h-auto"
                    />
                    <div className="absolute inset-0 bg-black/40 flex items-center justify-center text-white font-medium">
                      {index + 1}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Slide Viewer */}
        <div className="flex-grow flex flex-col">
          <div className="flex-grow flex items-center justify-center bg-gray-900 relative">
            <img
              src={slides[currentSlide]?.imageUrl}
              alt={`Slide ${currentSlide + 1}`}
              className="max-w-full max-h-full object-contain"
            />
            
            {/* Navigation Controls */}
            <button
              onClick={goToPrevSlide}
              disabled={currentSlide === 0}
              className={`absolute left-4 p-2 rounded-full bg-white/80 hover:bg-white ${
                currentSlide === 0 ? 'opacity-50 cursor-not-allowed' : ''
              }`}
            >
              <ChevronLeft className="h-6 w-6 text-gray-800" />
            </button>
            
            <button
              onClick={goToNextSlide}
              disabled={currentSlide === slides.length - 1}
              className={`absolute right-4 p-2 rounded-full bg-white/80 hover:bg-white ${
                currentSlide === slides.length - 1 ? 'opacity-50 cursor-not-allowed' : ''
              }`}
            >
              <ChevronRight className="h-6 w-6 text-gray-800" />
            </button>
            
            {/* Slide Counter */}
            <div className="absolute bottom-4 left-1/2 transform -translate-x-1/2 bg-black/70 text-white px-3 py-1 rounded-full text-sm">
              {currentSlide + 1} / {slides.length}
            </div>
          </div>

          {/* Speaker Notes */}
          {slides[currentSlide]?.notes && (
            <div className="bg-white border-t border-gray-200 p-4">
              <h3 className="text-sm font-medium text-gray-700 mb-2 flex items-center">
                <Presentation className="h-4 w-4 mr-2" />
                Speaker Notes
              </h3>
              <p className="text-gray-600 text-sm">{slides[currentSlide].notes}</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default SlideDeckPreview;